/**
 * sifirlama-talebi.tsx
 *
 * NE İŞE YARAR : Parola sıfırlama talebi — `AU-04`ü çağırır
 * KAYNAK SPEC  : 15-arayuz-standardi.md §15 (parola sıfırlama) · 04-api-sozlesmesi.md AU-04
 *                ADR-008 §8 (var olmayan hesap için de aynı yanıt) · ADR-019 §3
 * BAĞLI OLDUĞU : src/api/istemci.ts · src/metin
 * KULLANAN     : src/uygulama.tsx — `/parola/sifirlama-talebi` yolu
 * DİKKAT       : 1) EKRAN HER DURUMDA AYNI ŞEYİ SÖYLER (`04` AU-04 · ADR-008 §8).
 *                   Numara kayıtlı olsa da olmasa da, hesap kimlik sağlayıcısında olsa
 *                   da, saatlik sınır aşılmış olsa da sunucu `202` döner ve ekran aynı
 *                   onay panelini gösterir. Ayrım yapılsaydı bu ekran bir **hesap sayım
 *                   aracı**na dönerdi: numarayı yazan biri hesabın var olup olmadığını
 *                   öğrenirdi.
 *                2) ONAY METNİ *"gönderildi"* DEMEZ, *"kayıtlıysa gönderildi"* DER.
 *                   Kesin bir cümle, not 1'in kapattığı bilgiyi metinle geri açardı.
 *                3) TELEFON SORULUYOR, E-POSTA DEĞİL. `15` §15 bu satırda *"e-posta ile
 *                   bağlantı"* diyor ama `04` AU-04'ün girdisi `phone`tur ve akış yalnız
 *                   `auth_source = 'LOCAL'` hesaplar içindir — o hesaplar telefonla
 *                   açılır. Kimlik sağlayıcısındaki hesapların parola sıfırlaması
 *                   sağlayıcının kendi akışıdır. Arka uç yazılmış ve sınanmıştır; ekran
 *                   ona uyar. Fark `docs/DURUM.md`ye kaydedildi.
 *                4) BAĞLANTI 15 DAKİKA GEÇERLİDİR (ADR-008 §8) ve bu süre kullanıcıya
 *                   **söylenir**: bağlantıyı yarın açacak bir kullanıcı, süre yazılmazsa
 *                   sistemin bozuk olduğunu düşünür.
 *                5) BAĞLANTIYI GÖNDEREN TARAF HENÜZ YOK (AK-16). `AU-04` jetonu üretir
 *                   ama **ham hâlini döndürmez** (`kimlik.controller.ts` notu); gönderim
 *                   kurumun sürecidir ve sağlayıcı seçilmemiştir. Ekran bunu bilmez ve
 *                   bilmemelidir — gönderim yolu değiştiğinde bu dosya değişmez.
 * SON GÜNCELLEME: 2026-08-31 03:55:00
 */
import { Alert, Anchor, Button, Stack, Text, TextInput } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import { useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { ApiHatasi } from '../../api/hata';
import { istek } from '../../api/istemci';
import { metin } from '../../metin';
import { HataUyarisi } from '../ortak/hata-uyarisi';
import { KimlikDuzeni } from './duzen';

export function SifirlamaTalebiEkrani(): ReactElement {
  const [telefon, setTelefon] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [alindi, setAlindi] = useState(false);
  const [hataMesaji, setHataMesaji] = useState<string | null>(null);

  async function gonder(olay: FormEvent<HTMLFormElement>): Promise<void> {
    olay.preventDefault();
    if (gonderiliyor || telefon.trim() === '') {
      return;
    }

    setHataMesaji(null);
    setGonderiliyor(true);

    try {
      await istek({
        yol: '/auth/password/reset-request',
        yontem: 'POST',
        govde: { phone: telefon.trim() },
      });
      // Başlıktaki not 1 — yanıtın içeriğine bakılmaz; `202` tek sonuçtur.
      setAlindi(true);
    } catch (hata) {
      // Buraya yalnız ağ arızası ya da beklenmedik bir sunucu hatası düşer; hesabın
      // durumu hiçbir zaman buraya düşmez.
      setHataMesaji(hata instanceof ApiHatasi ? hata.message : metin('hata.beklenmeyen'));
    } finally {
      setGonderiliyor(false);
    }
  }

  const girisBaglantisi = (
    <Anchor component={Link} to="/giris" size="sm">
      {metin('sifirlamaTalebi.girisEkranina')}
    </Anchor>
  );

  if (alindi) {
    return (
      <KimlikDuzeni baslik={metin('sifirlamaTalebi.alindi')} altBilgi={girisBaglantisi}>
        <Alert color="bilgi" variant="light" icon={<IconCircleCheck size={18} />} radius="sm">
          {/* Başlıktaki not 2 — *"kayıtlıysa"* kelimesi kaldırılamaz. */}
          <Text size="sm">{metin('sifirlamaTalebi.alindiMetin')}</Text>
        </Alert>
      </KimlikDuzeni>
    );
  }

  return (
    <KimlikDuzeni
      baslik={metin('sifirlamaTalebi.baslik')}
      aciklama={metin('sifirlamaTalebi.aciklama')}
      altBilgi={girisBaglantisi}
    >
      <form onSubmit={(olay) => void gonder(olay)} noValidate>
        <Stack gap="md">
          <HataUyarisi mesaj={hataMesaji} />

          <TextInput
            label={metin('sifirlamaTalebi.telefon')}
            value={telefon}
            onChange={(olay) => setTelefon(olay.currentTarget.value)}
            autoComplete="tel"
            inputMode="tel"
            required
          />

          <Button type="submit" loading={gonderiliyor} fullWidth>
            {metin('sifirlamaTalebi.gonder')}
          </Button>
        </Stack>
      </form>
    </KimlikDuzeni>
  );
}
